/**
 * Pure transforms for the Product Map grouped sortable list.
 *
 * Everything here is UI/query-client agnostic on purpose — it's the part of
 * the drag-and-drop + coverage-roll-up behaviour most likely to have bugs, so
 * it's isolated for `productMapLogic.logic.test.ts` to exercise directly with
 * plain objects.
 *
 * @module features/product-map/productMapLogic
 */

import type { CapabilityCoverageStatus } from '@chatsdet/types';
import type { ChartTheme } from '@chatsdet/ui';
import type {
  MoveCapabilityInput,
  ProductAreaWithCapabilities,
  ProductCapability,
  ProductMapResponse,
} from './types';

// ---------------------------------------------------------------------------
// Droppable container ids
// ---------------------------------------------------------------------------

const AREA_CONTAINER_PREFIX = 'area-container:';
export const UNASSIGNED_CONTAINER_ID = `${AREA_CONTAINER_PREFIX}unassigned`;

/** Droppable id for an area's (possibly empty) capability list body. */
export function areaContainerId(areaId: string | null): string {
  return areaId ? `${AREA_CONTAINER_PREFIX}${areaId}` : UNASSIGNED_CONTAINER_ID;
}

/**
 * Parses a container droppable id back to its area id (`null` for
 * Unassigned), or `undefined` when `id` isn't a container id at all (i.e. a
 * plain capability id — the common case while hovering an existing row).
 */
export function parseContainerAreaId(id: string): string | null | undefined {
  if (id === UNASSIGNED_CONTAINER_ID) return null;
  if (id.startsWith(AREA_CONTAINER_PREFIX)) return id.slice(AREA_CONTAINER_PREFIX.length);
  return undefined;
}

// ---------------------------------------------------------------------------
// Optimistic re-nesting (pairs with `spliceIntoList` from boards/optimisticMove)
// ---------------------------------------------------------------------------

/** Regroups a flat, already-ordered capability list back into `{byArea, unassigned}`. */
export function regroupCapabilitiesByArea<T extends { id: string; areaId: string | null }>(
  flat: readonly T[]
): { byArea: Map<string, T[]>; unassigned: T[] } {
  const byArea = new Map<string, T[]>();
  const unassigned: T[] = [];
  for (const item of flat) {
    if (item.areaId) {
      const list = byArea.get(item.areaId);
      if (list) list.push(item);
      else byArea.set(item.areaId, [item]);
    } else {
      unassigned.push(item);
    }
  }
  return { byArea, unassigned };
}

/**
 * Applies a resolved capability move to the cached `GET /product-map`
 * response, for optimistic UI. Flattens the cached areas + unassigned bucket,
 * re-splices the moved capability via the shared board helper, then regroups
 * — mirrors `useProductItems.useMoveProductItem`'s `onMutate`, adapted for a
 * nested-by-area cache shape instead of one flat array per query.
 */
export function applyOptimisticCapabilityMove(
  previous: ProductMapResponse,
  input: MoveCapabilityInput,
  spliceIntoList: <T>(
    list: readonly T[],
    item: T,
    getId: (item: T) => string,
    target: { beforeItemId: string | null; afterItemId: string | null },
    isSameColumn?: (item: T) => boolean
  ) => T[]
): ProductMapResponse | null {
  const flat: ProductCapability[] = [
    ...previous.areas.flatMap((area) => area.capabilities),
    ...previous.unassignedCapabilities,
  ];
  const moving = flat.find((c) => c.id === input.id);
  if (!moving) return null;

  const updated: ProductCapability = { ...moving, areaId: input.areaId };
  const spliced = spliceIntoList(
    flat,
    updated,
    (c) => c.id,
    { beforeItemId: input.beforeCapabilityId, afterItemId: input.afterCapabilityId },
    (c) => c.areaId === input.areaId
  );
  const { byArea, unassigned } = regroupCapabilitiesByArea(spliced);

  return {
    ...previous,
    areas: previous.areas.map((area) => ({ ...area, capabilities: byArea.get(area.id) ?? [] })),
    unassignedCapabilities: unassigned,
  };
}

/** Optimistic area reorder — a plain whole-list `arrayMove`, no re-nesting needed. */
export function applyOptimisticAreaReorder(
  previous: ProductMapResponse,
  orderedIds: string[]
): ProductMapResponse {
  const byId = new Map(previous.areas.map((area) => [area.id, area]));
  const areas: ProductAreaWithCapabilities[] = orderedIds
    .map((id) => byId.get(id))
    .filter((area): area is ProductAreaWithCapabilities => Boolean(area));
  // Defensive: append any area the caller's orderedIds somehow omitted, so a
  // stale id list can never silently drop an area from the cache.
  for (const area of previous.areas) {
    if (!orderedIds.includes(area.id)) areas.push(area);
  }
  return { ...previous, areas };
}

// ---------------------------------------------------------------------------
// Coverage roll-up
// ---------------------------------------------------------------------------

export interface AreaCoverageRollup {
  covered: number;
  stale: number;
  knownIssue: number;
  uncovered: number;
  total: number;
}

/**
 * Per-area coverage counts. Deliberately returns raw counts only (no colors,
 * no percentage) — `AreaRow` decides presentation, and the honesty rule
 * requires callers to ALSO render `unmappedWorkflowCount` next to whatever
 * they derive from this.
 */
export function rollUpAreaCoverage(
  capabilities: ReadonlyArray<{ coverageStatus: CapabilityCoverageStatus }>
): AreaCoverageRollup {
  let covered = 0;
  let stale = 0;
  let knownIssue = 0;
  let uncovered = 0;
  for (const capability of capabilities) {
    if (capability.coverageStatus === 'covered') covered += 1;
    else if (capability.coverageStatus === 'stale') stale += 1;
    else if (capability.coverageStatus === 'known_issue') knownIssue += 1;
    else uncovered += 1;
  }
  return { covered, stale, knownIssue, uncovered, total: capabilities.length };
}

export interface AreaCoverageStats {
  mapped: number;
  /** covered / mapped — null when nothing is mapped yet. */
  passingPct: number | null;
  /**
   * mapped / total — null when the area has no capabilities.
   *
   * Deliberately named `mappedPct`, not `coveredPct`: `AreaCoverageRollup.covered`
   * already means "passing", so a `coveredPct` field here would be a second,
   * conflicting meaning of "covered" living right next to the first. The UI
   * copy still says "% of capabilities covered" (that's the product owner's
   * chosen wording, and it reads unambiguously once "passing" is used for the
   * other bucket) — only the internal field name avoids the collision.
   */
  mappedPct: number | null;
}

/**
 * Rounds a ratio to a whole percent for display.
 *
 * Honesty rule: never round UP to 100 unless the ratio is exactly 1 (clamp to
 * 99) — a bar reading "100%" implies nothing is left to fix. Never round DOWN
 * to 0 when the numerator is > 0 (clamp to 1) — a nonzero count reading "0%"
 * implies there's nothing there at all.
 *
 * `numerator === denominator` also matches 0/0, which would read as "100%" —
 * that's fine because every caller only invokes this with a `denominator > 0`
 * guard already in place (see `deriveAreaCoverageStats` below), so 0/0 never
 * reaches here.
 */
function honestPct(numerator: number, denominator: number): number {
  if (numerator === denominator) return 100;
  const raw = Math.round((numerator / denominator) * 100);
  if (raw >= 100) return 99;
  if (numerator > 0 && raw <= 0) return 1;
  return raw;
}

/** Derives the two header stat chips from a rollup — % of mapped capabilities passing, and % of all capabilities mapped. */
export function deriveAreaCoverageStats(rollup: AreaCoverageRollup): AreaCoverageStats {
  const mapped = rollup.covered + rollup.stale;
  return {
    mapped,
    passingPct: mapped > 0 ? honestPct(rollup.covered, mapped) : null,
    mappedPct: rollup.total > 0 ? honestPct(mapped, rollup.total) : null,
  };
}

/**
 * User-facing name for each coverage bucket. The bar, the chips and the row
 * dot all read from here.
 */
export const COVERAGE_STATUS_LABEL: Record<CapabilityCoverageStatus, string> = {
  covered: 'Passing',
  stale: 'Not passing',
  known_issue: 'Known issue',
  uncovered: 'Uncovered',
  blocked: 'Coverage blocked',
};

/**
 * Why a capability is in that bucket — the second line of every coverage
 * tooltip. `covered` needs no explanation.
 */
export const COVERAGE_STATUS_DETAIL: Record<CapabilityCoverageStatus, string | null> = {
  covered: null,
  stale: 'linked, but no recent pass',
  known_issue: 'linked flows red on an open linked issue',
  uncovered: 'no active flow linked',
  blocked: 'coverage can\'t be built yet (a blocked reason, or an open linked issue)',
};

/**
 * Badge tint per bucket for the drawer header — the `Badge` counterpart of
 * {@link coverageStatusColor} (success / warning / neutral), kept beside it so
 * the two tri-states are edited together.
 */
export const COVERAGE_STATUS_BADGE_VARIANT: Record<
  CapabilityCoverageStatus,
  'success' | 'warning' | 'outline' | 'destructive'
> = {
  covered: 'success',
  stale: 'warning',
  known_issue: 'destructive',
  uncovered: 'outline',
  blocked: 'destructive',
};

/**
 * The one status→chart-color mapping for this surface. The area coverage bar
 * and `CapabilityRow`'s dot both read it, so the two can never disagree about
 * what "not passing" looks like; the drawer badge uses
 * {@link COVERAGE_STATUS_BADGE_VARIANT}, the same tri-state in `Badge` terms.
 */
export function coverageStatusColor(
  palette: ChartTheme,
  status: CapabilityCoverageStatus
): string {
  if (status === 'covered') return palette.success;
  if (status === 'stale') return palette.warning;
  if (status === 'known_issue') return palette.destructive;
  return palette.neutral;
}

export interface CoverageBarSegmentCopy {
  status: CapabilityCoverageStatus;
  value: number;
  /** e.g. "2 of 3 mapped passing" — the tooltip's bold headline. */
  headline: string;
  /** The tooltip's second line, or null when there's nothing to add. */
  detail: string | null;
}

/**
 * The three coverage-bar segments' tooltip copy, in bar order. Pulled out as
 * a pure helper (rather than inlined JSX in `AreaRow`) so the exact wording
 * is unit-testable and can't silently diverge between the bar's three
 * segments.
 */
export function deriveAreaCoverageBarSegments(
  rollup: AreaCoverageRollup,
  stats: AreaCoverageStats
): CoverageBarSegmentCopy[] {
  return [
    {
      status: 'covered',
      value: rollup.covered,
      headline: `${rollup.covered} of ${stats.mapped} mapped passing`,
      // `passingPct` is non-null here in every case this segment is actually
      // rendered: `MultiSegmentBar` only shows a segment when `value > 0`,
      // and `rollup.covered > 0` implies `mapped > 0` implies (see
      // `deriveAreaCoverageStats`) `passingPct !== null`.
      detail: `${stats.passingPct}% of mapped capabilities`,
    },
    {
      status: 'stale',
      value: rollup.stale,
      headline: `${rollup.stale} of ${stats.mapped} mapped not passing`,
      detail: COVERAGE_STATUS_DETAIL.stale,
    },
    {
      status: 'known_issue',
      value: rollup.knownIssue,
      headline: `${rollup.knownIssue} of ${rollup.total} failing on a known issue`,
      detail: COVERAGE_STATUS_DETAIL.known_issue,
    },
    {
      status: 'uncovered',
      value: rollup.uncovered,
      headline: `${rollup.uncovered} of ${rollup.total} uncovered`,
      detail: COVERAGE_STATUS_DETAIL.uncovered,
    },
  ];
}

// ---------------------------------------------------------------------------
// Optimistic archive (delete)
// ---------------------------------------------------------------------------

/** Optimistic capability delete — drops it from wherever it currently renders. */
export function applyOptimisticCapabilityArchive(
  previous: ProductMapResponse,
  capabilityId: string
): ProductMapResponse {
  return {
    ...previous,
    areas: previous.areas.map((area) => ({
      ...area,
      capabilities: area.capabilities.filter((c) => c.id !== capabilityId),
    })),
    unassignedCapabilities: previous.unassignedCapabilities.filter((c) => c.id !== capabilityId),
  };
}

// ---------------------------------------------------------------------------
// Link roll-up (how many capabilities have at least one active flow linked)
// ---------------------------------------------------------------------------

export interface LinkRollup {
  linked: number;
  total: number;
}

/** Counts capabilities with `activeLinkCount > 0` — the header metric and `AreaRow` chips share this. */
export function rollUpLinkedCapabilities(
  capabilities: ReadonlyArray<{ activeLinkCount: number }>
): LinkRollup {
  let linked = 0;
  for (const capability of capabilities) {
    if (capability.activeLinkCount > 0) linked += 1;
  }
  return { linked, total: capabilities.length };
}

/**
 * Optimistic area delete. The server does NOT cascade: `DELETE
 * /product-map/areas/:id` is refused (409 `PRODUCT_MAP_STAGE_ORDER`) while the
 * area still has live capabilities, and the UI disables the button then. So
 * the only accepted case is an area with no live capabilities; this drops
 * just that area.
 */
export function applyOptimisticAreaArchive(
  previous: ProductMapResponse,
  areaId: string
): ProductMapResponse {
  return {
    ...previous,
    areas: previous.areas.filter((area) => area.id !== areaId),
  };
}

// ---------------------------------------------------------------------------
// Revision authorship
// ---------------------------------------------------------------------------

/** The one `actorLabel` value that names an actor a reader would recognize. */
const ASSISTANT_ACTOR_LABEL = 'Assistant';

/**
 * Who to credit for a revision in the drawer's history.
 *
 * `actorLabel` is NOT a display name — it is internal provenance, and most of
 * its values are unfit to show a user: the planner writes its prompt name
 * (`planning-draft`), and accepting a proposal writes `Accepted proposal
 * <uuid>`, which would render a raw id where a person's name belongs. Only the
 * assistant writes a label that reads as an actor, so only that one is
 * surfaced; everything else keeps the wording the drawer already used.
 */
export function revisionActorName(revision: {
  actorKind: string;
  actorLabel: string | null;
}): string {
  if (revision.actorKind === 'agent') return 'Planner';
  return revision.actorLabel === ASSISTANT_ACTOR_LABEL ? ASSISTANT_ACTOR_LABEL : 'A teammate';
}
